import React, { useRef, useState, useEffect, useMemo } from "react";
import { EditProfileImage } from "../../../components/superadmin";
import { TbTrashX } from "react-icons/tb";
import { BiCloudUpload } from "react-icons/bi";
import { useFormik } from "formik";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { handleApi } from "../../../utils/authentication";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { useSelector } from "react-redux";
import { EditAdminProfile } from "../../../components";
import { useDispatch } from "react-redux";
import { UPDATE_USER_DATA } from "../../../redux/features/user/userSlice";
import user_default from "../../../assests/user_default.png";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import LoadingSpinner from "../../../components/client/LoadingSpinner";

/**
 * Resolve the client's subscription state from the raw `subscription_end_date`.
 *
 *   none    → never subscribed (null / empty / unparseable)
 *   expired → had a subscription whose end date is in the past
 *   active  → has a subscription whose end date is in the future
 *
 * Avoids the `new Date(null) === 1970-01-01` JS quirk that previously made
 * never-subscribed clients look like "expired since 1970".
 */
const getSubscriptionState = (endDate) => {
  if (!endDate) return "none";
  const end = new Date(endDate);
  if (Number.isNaN(end.getTime())) return "none";
  return end > new Date() ? "active" : "expired";
};

const EditProfile = () => {
  const profileImageRef = useRef(null);
  const [imageUri, setImageUri] = useState("");

  const [show, setShow] = useState(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    website: "",
    email: "",
    client_name: "",
  });

  const user = useSelector((state) => state?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("client_profile");
  const [activeMembersCount, setActiveMembersCount] = useState(0);

  const subscriptionState = getSubscriptionState(
    initialValues?.subscription_end_date,
  );

  const getClientData = async () => {
    try {
      setInitialLoading(true);
      if (user?.client_id) {
        let method = "POST";
        let headers = {};
        let formdata = new FormData();
        formdata.append("client_id", user?.client_id);
        let result = await handleApi(`view_client`, method, headers, formdata);
        if (result?.success) {
          setInitialValues({
            first_name: result?.data?.client_info?.owner_first_name ?? "",
            last_name: result?.data?.client_info?.owner_last_name,
            website:
              result?.data?.client_info?.website &&
              result?.data?.client_info?.website != "null"
                ? result?.data?.client_info?.website
                : "",
            email: result?.data?.client_info?.owner_invite_email,
            client_name: result?.data?.client_info?.client_name,
            subscription_start_date:
              result?.data?.client_info?.subscription_start_date,
            subscription_end_date:
              result?.data?.client_info?.subscription_end_date,
            total_members: result?.data?.client_info?.total_members ?? 1,
            subscription_value: result?.data?.client_info?.subscription_value,
            is_blocked: result?.data?.client_info?.is_blocked,
          });
          setActiveMembersCount(
            result?.data?.client_info?.active_members_count ?? 0,
          );
          if (result?.data?.client_info?.client_logo_path) {
            setImageUri(result?.data?.client_info?.client_logo_path);
          }
        } else {
          toast.error("Failed to load customer details", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          return;
        }
      }
    } catch (e) {
      console.log(e);
      toast.error(e?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleDeleteImage = () => {
    if (imageUri) {
      setImageUri("");
    }
  };
  //reads the image and opens the modal by setting setShow-> true
  const handleImageUpload = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Only image files are allowed!");
        event.target.value = ""; // clear invalid file
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageUri(reader.result);
        setShow(true); // Set the image URI in state
      };
      reader.readAsDataURL(file); // Read the file as a data URL
      setFile(file);
    }
    event.target.value = "";
  };

  const handleClientDataUpdate = async () => {
    try {
      setLoading(true); // Start loading
      let method = "POST";
      let headers = {};
      let formData = new FormData();
      if (imageUri && file) {
        let response = await fetch(imageUri);
        let image_file = await response?.blob();
        formData.append("client_logo", image_file, file?.name);
      }
      // if (!imageUri) {
      //   toast.error(" Please Upload Logo ", {
      //     position: "top-right",
      //     autoClose: 5000,
      //     hideProgressBar: false,
      //     closeOnClick: true,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   })
      //   return

      // }
      formData.append("client_id", user?.client_id);
      formData.append("client_name", formik.values.client_name);
      formData.append("first_name", formik.values.first_name);
      formData.append("last_name", formik.values.last_name);
      formData.append("email", formik.values.email);
      formData.append("website", formik.values.website);
      if (formik.values.subscription_start_date) {
        formData.append(
          "subscription_start_date",
          format(new Date(formik.values.subscription_start_date), "yyyy-MM-dd"),
        );
      }
      if (formik.values.subscription_end_date) {
        formData.append(
          "subscription_end_date",
          format(new Date(formik.values.subscription_end_date), "yyyy-MM-dd"),
        );
      }
      formData.append("total_members", formik.values.total_members);
      formData.append("is_agency", user?.is_agency);
      formData.append("is_blocked", initialValues?.is_blocked);
      if (initialValues?.subscription_value != null) {
        formData.append("subscription_value", initialValues.subscription_value);
      }

      let result = await handleApi("edit_client", method, headers, formData);

      if (result?.success) {
        toast.success("Account Updated Successfully.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        dispatch(
          UPDATE_USER_DATA({
            client_name: result?.data?.client_info?.client_name,
            total_members: result?.data?.client_info?.total_members,
          }),
        );
        setInitialValues((prev) => ({
          ...prev,
          client_name: result?.data?.client_info?.client_name,
          total_members: result?.data?.client_info?.total_members,
        }));
      } else {
        toast.error(result?.error?.error_message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    } catch (e) {
      toast.error(e.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object({
        client_name: Yup.string().required("Client Name is Required"),
        first_name: Yup.string()
          .required("Firstname is Required")
          .matches(/^[A-Za-z]+$/, "Only letters are allowed"),
        last_name: Yup.string()
          .required("Lastname is Required")
          .matches(/^[A-Za-z]+$/, "Only letters are allowed"),
        email: Yup.string()
          .email("Invalid email address")
          .required("Email is Required"),
        total_members: Yup.number()
          .typeError("Members count must be a number")
          .required("Members count is required")
          .integer("Members count must be a whole number")
          .min(
            Math.max(activeMembersCount, 1),
            activeMembersCount > 0
              ? `Cannot be less than ${activeMembersCount} active member(s)`
              : "Must be at least 1",
          ),
      }),
    [activeMembersCount],
  );

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: () => {
      handleClientDataUpdate();
    },
  });
  useEffect(() => {
    getClientData();
  }, [user?.client_id]);

  useEffect(() => {
    formik.setValues(initialValues);
  }, [initialValues]);
  if (user?.role_id > 2) {
    return <Navigate to="user_profile" replace={true} />;
  }

  return (
    <div className="">
      {/* <div className="row campaign_header_tab d-flex align-items-center  my-4 px-4 ">
                <div className="col-lg-10 col-md-12 row ">
                <span href="" className="col-lg-2 col-md-3 " onClick={()=>setActiveTab('client_profile')}>
                        <p className={`mb-0 ${activeTab==="client_profile" && "campaign_header_tab_active"}`}>Client Profile</p>
                        {activeTab==="client_profile" &&
                        <div style={{ border: 'solid 1px #7f56d9'}}> </div>}
                    </span>
                    <span href="" className="col-lg-2 col-md-3 " onClick={()=>setActiveTab('admin_profile')}>
                        <p className={`mb-0 ${activeTab==="admin_profile" && "campaign_header_tab_active"}`}>User Profile</p>
                        {activeTab==="admin_profile" &&
                        <div style={{ border: 'solid 1px #7f56d9'}}> </div>}
                    </span>
                   
                 
                    <div className="col-lg-4 col-md-4">

                    </div>

                </div>
          

            </div> */}
      {activeTab === "admin_profile" && <EditAdminProfile />}
      {activeTab === "client_profile" && (
        <form onSubmit={formik.handleSubmit}>
          {!user?.client_id ? (
            <div className="d-flex justify-content-center">
              <p
                style={{
                  fontFamily: "aeonik_medium",
                }}
                className="fs-5"
              >
                Please select client(Organisation) to view client details
              </p>
            </div>
          ) : (
            <div className="position-relative d-flex flex-column">
              {initialLoading && <LoadingSpinner />}

              {subscriptionState === "none" && (
                <div
                  className="alert alert-light border mb-3 py-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2"
                  style={{ borderColor: "#E9D7FE" }}
                >
                  <div>
                    <span className="fw-semibold small" style={{ color: "#101828" }}>
                      No active subscription yet.
                    </span>
                    <span className="d-block text-muted small">
                      You can still update your organization profile below.
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary rounded-pill px-4 flex-shrink-0"
                    onClick={() => navigate("/client/settings/payments")}
                  >
                    Go to Payments
                  </button>
                </div>
              )}

              <EditProfileImage
                show={show}
                setShow={setShow}
                uri={imageUri}
                setImageUri={setImageUri}
                aspect_ratio={null}
              />
              <div className="card border profile_card">
                <div>
                  <h5 className="pb-0">Edit Client Profile</h5>
                </div>
                {/* profile image */}
                <div className="row border-1 border_bottom pb-4">
                  <div className="col-xl-2 d-flex justify-content-center justify-content-xl-start   ">
                    <div
                      style={{
                        width: "64px",
                        height: "auto",
                        borderRadius: "80%",
                      }}
                    >
                      <img
                        src={imageUri ? imageUri : user_default} // Use the image URI for the src
                        alt="Profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>

                    <input
                      ref={profileImageRef}
                      type="file"
                      id="profile_image"
                      className="d-none"
                      accept="image/*"
                      onChange={handleImageUpload} // Add the onChange handler
                    />
                  </div>
                  <div className=" col-xl-6 row d-flex align-items-end  ">
                    <div className="col-12 col-md-12 col-lg-6 mb-2">
                      <button
                        type="button"
                        disabled={imageUri ? false : true}
                        className={`w-100 ${!imageUri && "disable_btn"} btn btn-outline-custom btn-outline-primary  rounded-pill d-flex  justify-content-center align-items-center gap-1`}
                        onClick={handleDeleteImage}
                      >
                        <TbTrashX /> Delete Image
                      </button>
                    </div>
                    <div className="col-12 col-md-12 col-lg-6  mb-2 ">
                      <button
                        type="button"
                        onClick={() => {
                          profileImageRef.current.click();
                        }}
                        className="w-100 py-2 gap-1 btn btn-primary rounded-pill d-flex   justify-content-center  align-items-center  "
                      >
                        {" "}
                        <BiCloudUpload /> Upload Profile Image
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mb-1 row">
                  <label
                    htmlFor="client_name"
                    className="col-lg-3  col-form-label"
                  >
                    Client Name*
                  </label>
                  <div className="col-lg-6 ">
                    <input
                      type="text"
                      className="form-control"
                      id="client_name"
                      placeholder="Client Name"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.client_name}
                    />
                    {formik.touched.client_name && formik.errors.client_name ? (
                      <div className="text-danger text-start small">
                        {formik.errors.client_name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mb-1 row d-none">
                  <label
                    htmlFor="first_name"
                    className="col-lg-3  col-form-label"
                  >
                    First Name*
                  </label>
                  <div className="col-lg-6 ">
                    <input
                      type="text"
                      className="form-control"
                      id="first_name"
                      placeholder="Firstname"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.first_name}
                    />
                    {formik.touched.first_name && formik.errors.first_name ? (
                      <div className="text-danger text-start small">
                        {formik.errors.first_name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mb-1 row  d-none">
                  <label
                    htmlFor="last_name"
                    className="col-lg-3 col-form-label"
                  >
                    Last Name*
                  </label>
                  <div className="col-lg-6 ">
                    <input
                      type="text"
                      className="form-control"
                      id="last_name"
                      placeholder="Lastname"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.last_name}
                    />
                    {formik.touched.last_name && formik.errors.last_name ? (
                      <div className="text-danger text-start small">
                        {formik.errors.last_name}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mb-1 row">
                  <label htmlFor="email" className="col-lg-3 col-form-label">
                    Email*
                  </label>
                  <div className="col-lg-6 ">
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      placeholder="Email"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      disabled
                    />
                    {formik.touched.email && formik.errors.email ? (
                      <div className="text-danger text-start small">
                        {formik.errors.email}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mb-1 row">
                  <label htmlFor="website" className="col-lg-3 col-form-label">
                    Website
                  </label>
                  <div className="col-lg-6 ">
                    <input
                      type="website"
                      className="form-control"
                      id="website"
                      placeholder="Website"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.website}
                    />
                    {formik.touched.website && formik.errors.website ? (
                      <div className="text-danger text-start small">
                        {formik.errors.website}
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="mb-1 row">
                  <label
                    htmlFor="subscription_start_date"
                    className="col-lg-3 col-form-label"
                  >
                    Subscription Start Date
                  </label>
                  <div className="col-lg-6">
                    {formik.values.subscription_start_date ? (
                      <DatePicker
                        showIcon={false}
                        selected={formik.values.subscription_start_date}
                        onChange={() => {}}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select Start Date"
                        className="form-control"
                        disabled
                      />
                    ) : (
                      <input
                        type="text"
                        id="subscription_start_date"
                        className="form-control"
                        value="N/A"
                        disabled
                        readOnly
                      />
                    )}
                  </div>
                </div>
                <div className="mb-1 row">
                  <label
                    htmlFor="subscription_end_date"
                    className="col-lg-3 col-form-label"
                  >
                    Subscription Expiration Date
                  </label>
                  <div className="col-lg-6">
                    {formik.values.subscription_end_date ? (
                      <DatePicker
                        showIcon={false}
                        selected={formik.values.subscription_end_date}
                        onChange={() => {}}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Select Subscription Expiration Date"
                        className="form-control"
                        disabled
                      />
                    ) : (
                      <input
                        type="text"
                        id="subscription_end_date"
                        className="form-control"
                        value="N/A"
                        disabled
                        readOnly
                      />
                    )}
                  </div>
                </div>
                <div className="mb-1 row">
                  <label
                    htmlFor="total_members"
                    className="col-lg-3 col-form-label"
                  >
                    Members Count*
                  </label>
                  <div className="col-lg-6">
                    <input
                      type="number"
                      name="total_members"
                      min={Math.max(activeMembersCount, 1)}
                      step={1}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.total_members ?? ""}
                      className="form-control"
                      id="total_members"
                      placeholder="Enter members count"
                    />
                    <div className="form-text">
                      {activeMembersCount > 0
                        ? `Minimum ${activeMembersCount} (current active members). Increase to invite more team members.`
                        : "Set how many team members this organization can have."}
                    </div>
                    {formik.touched.total_members &&
                    formik.errors.total_members ? (
                      <div className="text-danger text-start small">
                        {formik.errors.total_members}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="py-2 row">
                  {(user?.role_id === 1 || user?.role_id === 2) &&
                    user?.status && (
                      <div className="col-md-9 d-flex justify-content-end">
                        {!loading ? (
                          <button
                            type="submit"
                            className="btn btn-primary rounded-pill px-5"
                          >
                            Update Details
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary rounded-pill px-5"
                            disabled
                          >
                            Updating...
                          </button>
                        )}
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}
        </form>
      )}
    </div>
  );
};

export default EditProfile;
